import api, { API } from '@/utils/api.utils';

export interface Activity {
  id: number;
  user_id: number;
  activity_type: string;
  position_description: string;
  organization_name?: string;
  activity_description: string;
  participation_grades: string[];
  timing_of_participation: string;
  hours_per_week: number | null;
  weeks_per_year: number | null;
  intend_to_participate_in_college: boolean;
  confidence_score: number;
  raw_description?: string;
  order_index: number;
}

export interface ActivityAnalysisRequest {
  description: string;
}

export interface ActivityAnalysisResponse {
  activity: Activity;
  confidence_score: number;
  missing_info: string[];
}

export interface ActivitiesListResponse {
  activities: Activity[];
  total_count: number;
}

export interface ActivityCreateRequest {
  activity_type: string;
  position_description: string;
  organization_name?: string;
  activity_description: string;
  participation_grades: string[];
  timing_of_participation: string;
  hours_per_week: number | null;
  weeks_per_year: number | null;
  intend_to_participate_in_college: boolean;
  raw_description?: string;
  order_index?: number;
}

export interface ActivityUpdateRequest {
  activity_type?: string;
  position_description?: string;
  organization_name?: string;
  activity_description?: string;
  participation_grades?: string[];
  timing_of_participation?: string;
  hours_per_week?: number | null;
  weeks_per_year?: number | null;
  intend_to_participate_in_college?: boolean;
  order_index?: number;
}

export interface ActivityReorderRequest {
  activity_orders: Array<{ id: number; order_index: number }>;
}

export interface ActivityReorderResponse {
  success: boolean;
  message: string;
}

class ActivityService {
  private baseURL = `${API.BASE_URL}/api/v1/activities`;

  /**
   * Analyze a natural language activity description using AI
   */
  async analyzeActivity(request: ActivityAnalysisRequest): Promise<ActivityAnalysisResponse> {
    try {
      const response = await api.post(`${this.baseURL}/analyze`, request);
      return response.data;
    } catch (error) {
      console.error('Error analyzing activity:', error);
      throw error;
    }
  }

  /**
   * Create a new activity
   */
  async createActivity(activityData: ActivityCreateRequest): Promise<Activity> {
    try {
      const response = await api.post(this.baseURL, activityData);
      return response.data;
    } catch (error) {
      console.error('Error creating activity:', error);
      throw error;
    }
  }

  /**
   * Get all activities for the current user
   */
  async getUserActivities(): Promise<ActivitiesListResponse> {
    try {
      const response = await api.get(this.baseURL);
      return response.data;
    } catch (error) {
      console.error('Error fetching activities:', error);
      throw error;
    }
  }

  /**
   * Update an existing activity
   */
  async updateActivity(activityId: number, activityData: ActivityUpdateRequest): Promise<Activity> {
    try {
      const response = await api.put(`${this.baseURL}/${activityId}`, activityData);
      return response.data;
    } catch (error) {
      console.error('Error updating activity:', error);
      throw error;
    }
  }

  /**
   * Delete an activity
   */
  async deleteActivity(activityId: number): Promise<void> {
    try {
      await api.delete(`${this.baseURL}/${activityId}`);
    } catch (error) {
      console.error('Error deleting activity:', error);
      throw error;
    }
  }

  /**
   * Get suggestions for missing activity information
   */
  async getMissingActivityInfo(): Promise<string[]> {
    try {
      const response = await api.get(`${this.baseURL}/missing-info`);
      return response.data;
    } catch (error) {
      console.error('Error fetching missing activity info:', error);
      return [];
    }
  }

  /**
   * Reorder activities by updating their order_index values
   */
  async reorderActivities(request: ActivityReorderRequest): Promise<ActivityReorderResponse> {
    try {
      const response = await api.post(`${this.baseURL}/reorder`, request);
      return response.data;
    } catch (error) {
      console.error('Error reordering activities:', error);
      throw error;
    }
  }
}

export default new ActivityService();